import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { IStore } from "redux/store";

interface ITankInfo {
	tankType: string;
	capacity: string | number;
	dailyConsumption: string | number;
	robOnboard: string | number;
	lastBunker: {
		date: string;
		location: string;
		time?: string;
	} | null;
	remarks: string;
	statusColor?: "green" | "blue" | "yellow";
}

interface IChemicalROBSData {
	id: string;
	chemicalType: string;
	capacityPercentage: string;
	tanks: ITankInfo[];
}

const chemicalData: IChemicalROBSData[] = [
	{
		id: "1",
		chemicalType: "NaOH (Scrubber)",
		capacityPercentage: "95%",
		tanks: [
			{
				tankType: "Portside storage",
				capacity: "-",
				dailyConsumption: "-",
				robOnboard: "-",
				lastBunker: null,
				remarks: "-",
			},
			{
				tankType: "STBD side storage",
				capacity: "-",
				dailyConsumption: "-",
				robOnboard: "-",
				lastBunker: null,
				remarks: "-",
			},
			{
				tankType: "Only one tank",
				capacity: 42.75,
				dailyConsumption: "Total",
				robOnboard: 33.07,
				statusColor: "green",
				lastBunker: null,
				remarks: "-",
			},
		],
	},
	{
		id: "2",
		chemicalType: "Urea (SCR)",
		capacityPercentage: "95%",
		tanks: [
			{
				tankType: "Storage tank",
				capacity: "-",
				dailyConsumption: 0.0,
				robOnboard: 30,
				statusColor: "blue",
				lastBunker: { date: "07 Nov 24", location: "USA", time: "3500 ltrs" },
				remarks: "In urea tank",
			},
			{
				tankType: "IBC plastic container",
				capacity: "-",
				dailyConsumption: "-",
				robOnboard: 26,
				statusColor: "blue",
				lastBunker: { date: "07 Nov 24", location: "USA", time: "3500 ltrs" },
				remarks: "IBC plastic containers",
			},
			{
				tankType: "Both tanks",
				capacity: 38,
				dailyConsumption: "Total",
				robOnboard: 56,
				statusColor: "yellow",
				lastBunker: null,
				remarks: "Only used in ECA",
			},
		],
	},
	{
		id: "3",
		chemicalType: "BWTS",
		capacityPercentage: "95%",
		tanks: [
			{
				tankType: "Steering gear storage",
				capacity: "NA",
				dailyConsumption: 0.0,
				robOnboard: "3700 kg",
				lastBunker: { date: "27 July 22", location: "Singapore" },
				remarks: "6000 kg recieved",
			},
		],
	},
];

const StatusIndicator: React.FC<{ color?: "green" | "blue" | "yellow" }> = ({
	color,
}) => {
	if (!color) return null;

	const colorClasses = {
		green: "bg-green-500",
		blue: "bg-blue-500",
		yellow: "bg-yellow-500",
	};

	return (
		<div
			className={`w-1.5 h-1.5 rounded-full ${colorClasses[color]} mr-1`}
		></div>
	);
};

const BunkerBadge: React.FC<{ bunkerInfo: ITankInfo["lastBunker"] }> = ({
	bunkerInfo,
}) => {
	if (!bunkerInfo) return <span className="text-gray-500">-</span>;

	const { date, location, time } = bunkerInfo;
	return (
		<div className="bg-red-100 text-red-700 text-xs font-medium px-3 py-1.5 rounded-md whitespace-nowrap inline-flex items-center gap-x-1">
			<span className="inline-block">{date}</span>
			<span className="w-1.5 h-1.5 bg-red-500 rounded-full inline-block" />
			<span className="inline-block">{location}</span>
			<span className="w-1.5 h-1.5 bg-red-500 rounded-full inline-block" />
			<span className="inline-block">{time && `${time}`}</span>
		</div>
	);
};

type OwnProps = {};
type ChemicalROBSTableProps = PropsFromRedux & OwnProps;
function ChemicalROBSTable(props: ChemicalROBSTableProps) {
	return (
		<div className="bg-white rounded-lg shadow-sm overflow-x-auto mt-6 border border-gray-200">
			<table className="w-full border-collapse text-[12px] xl:text-sm">
				<thead>
					<tr className="text-left text-gray-500 font-medium">
						<th className="p-2 font-semibold whitespace-nowrap text-xs xl:text-[12px]">Chemical Type</th>
						<th className="p-2 font-semibold whitespace-nowrap text-xs xl:text-[12px]">Capacity (%)</th>
						<th className="p-2 font-semibold whitespace-nowrap text-xs xl:text-[12px]">Tank Type</th>
						<th className="p-2 font-semibold whitespace-nowrap text-xs xl:text-[12px]">Capacity (M3)</th>
						<th className="p-2 font-semibold whitespace-nowrap text-xs xl:text-[12px]">
							Daily Consumption
						</th>
						<th className="p-2 font-semibold whitespace-nowrap text-xs xl:text-[12px]">ROB Onboard</th>
						<th className="p-2 font-semibold whitespace-nowrap text-xs xl:text-[12px]">
							Last Bunker Date/Port
						</th>
						<th className="p-2 font-semibold whitespace-nowrap text-xs xl:text-[12px]">Remarks</th>
					</tr>
				</thead>
				<tbody>
					{chemicalData.map((chemical) => (
						<React.Fragment key={chemical.id}>
							{chemical.tanks.map((tank, tankIndex) => {
								const isFirstRowOfGroup = tankIndex === 0;
								const rowId = `${chemical.id}-${tankIndex}`;

								return (
									<tr
										key={rowId}
										className={`border-t border-gray-200 hover:bg-gray-50 cursor-pointer `}
									>
										{/* Chemical Type - Spans multiple rows */}
										{isFirstRowOfGroup && (
											<td
												className="p-2 font-medium text-gray-700 align-top whitespace-nowrap"
												rowSpan={chemical.tanks.length}
											>
												{chemical.chemicalType}
											</td>
										)}
										{/* Capacity % - Spans multiple rows */}
										{isFirstRowOfGroup && (
											<td
												className="p-2 border-l text-gray-700 align-top whitespace-nowrap "
												rowSpan={chemical.tanks.length}
											>
												{chemical.capacityPercentage}
											</td>
										)}
										<td
											className={`border-l border-gray-200 text-gray-800 whitespace-nowrap`}
										>
											<div className="p-2 flex items-center h-full border-gray-200">
												<span>{tank.tankType}</span>
											</div>
										</td>
										<td className="p-2 border-l text-gray-700 whitespace-nowrap">
											{tank.capacity}
										</td>
										<td className="p-2 border-l text-gray-700 whitespace-nowrap">
											{tank.dailyConsumption}
										</td>
										<td className="p-2 border-l text-gray-700 text-[12px] whitespace-nowrap">
											<div
												className={`inline-flex items-center bg-${tank.statusColor}-100 px-2 rounded`}
											>
												<StatusIndicator color={tank.statusColor} />
												{tank.robOnboard}
											</div>
										</td>
										<td className="p-2 border-l whitespace-nowrap">
											<BunkerBadge bunkerInfo={tank.lastBunker} />
										</td>

										<td className="p-2 border-l text-gray-700 whitespace-nowrap">
											{tank.remarks}
										</td>
									</tr>
								);
							})}
						</React.Fragment>
					))}
				</tbody>
			</table>
		</div>
	);
}
function mapStateToProps(store: IStore, ownProps: OwnProps) {
	return {};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(ChemicalROBSTable);
